import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, X, Search, AlertCircle, Loader2, Users, Eye, Phone, Mail, Printer
} from 'lucide-react';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEAD_STATUSES = ['Open', 'Received', 'Under Inspection', 'Inspection Complete', 'Quotation Sent', 'Approved'];

const STATUS_STYLES = {
  'Open': 'bg-blue-100 text-blue-700',
  'Received': 'bg-purple-100 text-purple-700',
  'Under Inspection': 'bg-amber-100 text-amber-700',
  'Inspection Complete': 'bg-cyan-100 text-cyan-700',
  'Quotation Sent': 'bg-orange-100 text-orange-700',
  'Approved': 'bg-green-100 text-green-700',
};

const initialFormData = { name: '', email: '', phone: '', product: '', requirements: '', remarks: '' };

export const Leads = () => {
  const perms = detectPerms();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try { setLoading(true); const res = await axios.get(`${BACKEND_URL}/api/leads`, getAuthHeaders()); setItems(res.data); }
    catch (err) { setError('Failed to fetch leads'); } finally { setLoading(false); }
  };

  const handleInputChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };

  const openAddModal = () => { setFormData(initialFormData); setEditingItem(null); setError(''); setShowModal(true); };

  const openEditModal = (item) => {
    setFormData({ name: item.name || '', email: item.email || '', phone: item.phone || '', product: item.product || '', requirements: item.requirements || '', remarks: item.remarks || '' });
    setEditingItem(item); setError(''); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (!formData.name.trim()) { setError('Name is required'); return; }
    setSubmitting(true);
    try {
      const payload = { ...formData };
      ['email', 'phone', 'product', 'requirements', 'remarks'].forEach(k => { if (!payload[k]?.trim()) payload[k] = null; });
      if (editingItem) { await axios.put(`${BACKEND_URL}/api/leads/${editingItem.id}`, payload, getAuthHeaders()); }
      else { await axios.post(`${BACKEND_URL}/api/leads`, payload, getAuthHeaders()); }
      setShowModal(false); fetchItems();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save lead'); } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${BACKEND_URL}/api/leads/${id}`, getAuthHeaders()); setDeleteConfirm(null); fetchItems(); }
    catch (err) { setError('Failed to delete lead'); }
  };

  const filtered = items.filter(i => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = i.name?.toLowerCase().includes(q) || i.email?.toLowerCase().includes(q) || i.phone?.includes(searchQuery) || i.product?.toLowerCase().includes(q);
    return matchesSearch && (statusFilter === 'All' || i.status === statusFilter);
  });

  const statusCounts = LEAD_STATUSES.reduce((acc, s) => { acc[s] = items.filter(i => i.status === s).length; return acc; }, {});

  const printBarcode = useCallback((lead) => {
    const printWin = window.open('', '_blank', 'width=400,height=300');
    printWin.document.write(`<!DOCTYPE html><html><head><title>Barcode - ${lead.name}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; display: flex; justify-content: center; padding: 16px; }
        .barcode-card { border: 1px solid #ccc; padding: 10px 16px; text-align: center; width: 260px; }
        .customer { font-size: 10px; color: #555; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.5px; }
        .phone { font-size: 9px; color: #777; margin-bottom: 4px; }
        svg { display: block; margin: 0 auto; }
        .lead-text { font-size: 9px; color: #333; margin-top: 2px; font-family: monospace; }
        @media print { body { -webkit-print-color-adjust: exact; } }
      </style></head><body><div class="barcode-card">
        <div class="customer">${lead.name || ''}</div>
        <div class="phone">${lead.phone || ''}</div>
        <div id="bc"></div>
        <div class="lead-text">${lead.id}</div>
      </div></body></html>`);
    printWin.document.close();
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = printWin.document.createElementNS(svgNS, 'svg');
    try {
      JsBarcode(svg, lead.id, { format: 'CODE128', width: 1.5, height: 40, displayValue: false, margin: 2 });
    } catch { return; }
    const bcEl = printWin.document.getElementById('bc');
    if (bcEl) bcEl.appendChild(svg);
    setTimeout(() => { printWin.print(); }, 300);
  }, []);

  return (
    <div data-testid="leads-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Leads</h1>
          <p className="text-slate-600 mt-1">Manage your service leads</p>
        </div>
        <button onClick={openAddModal} data-testid="add-lead-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
          <Plus className="h-5 w-5" /> Add Lead
        </button>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
        {LEAD_STATUSES.map(s => (
          <button key={s} onClick={() => setStatusFilter(statusFilter === s ? 'All' : s)}
            data-testid={`status-filter-${s.toLowerCase().replace(/\s+/g, '-')}`}
            className={`bg-white rounded-sm border p-3 text-left transition-colors ${statusFilter === s ? 'border-orange-500 ring-1 ring-orange-500' : 'border-slate-200 hover:border-slate-300'}`}>
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-1 ${STATUS_STYLES[s]}`}>{s}</span>
            <p className="text-xl font-bold text-slate-900">{statusCounts[s] || 0}</p>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by name, email, phone or product..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {error && !showModal && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Users className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No leads found</h3>
            <p className="text-slate-500 mb-6">{items.length === 0 ? 'Start by adding your first lead' : 'Try adjusting your search or filter'}</p>
            {items.length === 0 && (
              <button onClick={openAddModal} className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600"><Plus className="h-5 w-5" /> Add First Lead</button>
            )}
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-9 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Name</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Phone</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Product</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Assigned To</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map(item => (
              <div key={item.id} data-testid={`lead-row-${item.id}`}
                className="grid grid-cols-1 md:grid-cols-9 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="col-span-2">
                  <span className="font-medium text-slate-900">{item.name}</span>
                  {item.email && <span className="block text-xs text-slate-500">{item.email}</span>}
                </div>
                <div className="text-slate-600 text-sm">{item.phone || '-'}</div>
                <div className="text-slate-600 text-sm">{item.product || '-'}</div>
                <div className="text-sm">{item.assigned_to_name ? <span className="text-orange-600 font-medium">{item.assigned_to_name}</span> : <span className="text-slate-400">None</span>}</div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${STATUS_STYLES[item.status] || 'bg-slate-100 text-slate-700'}`}>{item.status}</span>
                </div>
                <div className="text-slate-500 text-xs">{item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => navigate(`/admin/services/leads/${item.id}`)} data-testid={`view-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"><Eye className="h-4 w-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); printBarcode(item); }} data-testid={`print-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors" title="Print Barcode"><Printer className="h-4 w-4" /></button>
                  <>
                  {perms.canEdit && (
                  <button onClick={() => openEditModal(item)} data-testid={`edit-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"><Pencil className="h-4 w-4" /></button>
                  )}
                  {perms.canDelete && (
                  <button onClick={() => setDeleteConfirm(item.id)} data-testid={`delete-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"><Trash2 className="h-4 w-4" /></button>
                  )}
                  </>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this lead?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">{editingItem ? 'Edit Lead' : 'Add New Lead'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              )}
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6"><AlertCircle className="h-5 w-5" /><span>{error}</span></div>}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name <span className="text-red-500">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} required data-testid="input-name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} data-testid="input-email"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                    <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} data-testid="input-phone"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Product</label>
                  <input type="text" name="product" value={formData.product} onChange={handleInputChange} data-testid="input-product"
                    placeholder="Enter product name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Requirements</label>
                  <textarea name="requirements" value={formData.requirements} onChange={handleInputChange} rows={3} data-testid="input-requirements"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Remarks</label>
                  <textarea name="remarks" value={formData.remarks} onChange={handleInputChange} rows={2} data-testid="input-remarks"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}{editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
